<script setup lang="ts">
import { ref, PropType } from 'vue';
import { useForm, usePage } from '@inertiajs/vue3';
import { toast } from 'vue3-toastify';
import { SharedPageProps } from '@/Types/inertia';
// import route from 'ziggy-js'

type Permission = "read" | "create" | "update" | "delete";
type PermissionsArray = Permission[];
type Token = {
    id: number,
    name: string,
    abilities: PermissionsArray,
    last_used_ago?: any,
};
interface PageData extends SharedPageProps{
    tokens: Token[],
    availablePermissions: PermissionsArray,
    defaultPermissions: PermissionsArray,
}
const page = usePage<PageData>()

const createApiTokenForm = useForm<{
    name: string,
    permissions: PermissionsArray
}>({
    name: '',
    permissions: page.props.defaultPermissions,
});

const updateApiTokenForm = useForm<{
    permissions: PermissionsArray
}>({
    permissions: [],
});

const deleteApiTokenForm = useForm({});

const displayingToken = ref(false);
const updateingApiToken = ref(false);
const deletingApiToken = ref(false);
const managingPermissionsFor = ref<Token | null>();
const apiTokenBeingDeleted = ref<Token | null>();

const createApiToken = () => {
    createApiTokenForm.post(route('api-tokens.store'), {
        preserveScroll: true,
        onSuccess: () => {
            displayingToken.value = true;
            createApiTokenForm.reset();
        },
    });
};

const manageApiTokenPermissions = (token: Token) => {
    updateApiTokenForm.permissions = token.abilities;
    managingPermissionsFor.value = token;
    updateingApiToken.value = true;
    console.log(managingPermissionsFor.value)
};

const updateApiToken = () => {
    updateApiTokenForm.put(route('api-tokens.update', managingPermissionsFor.value ?? {}), {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
            managingPermissionsFor.value = null;
            updateingApiToken.value = false;
            toast.success("Token updated");
        },
    });
};


const confirmApiTokenDeletion = async (token: Token) => {
    apiTokenBeingDeleted.value = token;
    deletingApiToken.value = true;
};

const deleteApiToken = () => {
    deleteApiTokenForm.delete(route('api-tokens.destroy', apiTokenBeingDeleted.value ?? {}), {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
            apiTokenBeingDeleted.value = null;
            deletingApiToken.value = false;
            toast.success("Token Deleted");
        },
    });
};


</script>

<template>
    <div>
        <!-- Generate API Token -->
        <div>
            <form @submit.prevent="createApiToken">
                <v-row>
                    <v-col cols="12" sm="4">
                        <div class="text-h6 pb-1">Create API Token</div>
                        <div class="text-body-2 text-secondary">API tokens allow third-party services to authenticate with our application on your behalf.</div>
                    </v-col>
                    <v-col cols="12" sm="8">
                        <v-card>
                            <v-card-text>
                                <div class="d-flex flex-column ga-2 pt-1">
                                    <v-row>
                                        <v-col cols="12" md="8" xl="7" class="d-flex flex-column ga-4">
                                            <div>
                                                <v-text-field-v1
                                                    id="name"
                                                    label="Token Name"
                                                    v-model="createApiTokenForm.name"
                                                    placeholder=""
                                                    :error-messages="createApiTokenForm.errors.name"
                                                    required
                                                ></v-text-field-v1>
                                            </div>
                                            <div>
                                                <form-label title="Permissions" ></form-label>
                                                <div class="mx-n2">
                                                    <v-row dense>
                                                        <v-col cols="6" v-for="permission in page.props.availablePermissions" :key="permission">
                                                            <v-checkbox color="primary" :label="permission" hide-details v-model="createApiTokenForm.permissions" :value="permission"></v-checkbox>
                                                        </v-col>
                                                    </v-row>
                                                </div>
                                            </div>
                                        </v-col>
                                    </v-row>
                                </div>
                                
                            </v-card-text>
                            <v-card-actions-default>
                                <div class="d-flex justify-space-between align-center flex-grow-1 ga-4">
                                    <div>
                                        <span class="text-success" v-if="createApiTokenForm.recentlySuccessful">
                                            <span class="fa fa-check"></span> Created
                                        </span>
                                    </div>
                                    <v-btn type="submit" color="primary" class="match-input-compact" text="Create" :disabled="createApiTokenForm.processing" :loading="createApiTokenForm.processing">
                                        <template #prepend>
                                            <mdi-icon>check</mdi-icon>
                                        </template>
                                    </v-btn>
                                </div>
                            </v-card-actions-default>
                        </v-card>
                    </v-col>
                </v-row>
            </form>
        </div>

        <!-- Token Value Modal -->
        <div  v-if="page.props.tokens && page.props.tokens.length > 0">
            <div class="py-10">
                <v-divider></v-divider>
            </div>
            <v-row>
                <v-col cols="12" sm="4">
                    <div class="text-h6 pb-1">Manage API Tokens</div>
                    <div class="text-body-2 text-secondary">You may delete any of your existing tokens if they are no longer needed.</div>
                </v-col>
                <v-col cols="12" sm="8">
                    <v-card>
                        <v-card-text class="pa-0">
                            <div class="d-flex flex-column ga-2 pt-1">
                                <v-list class="pa-0">
                                    <template v-for="(token, index) in page.props.tokens" :key="token.id">
                                        <v-divider v-if="index > 0"></v-divider>
                                        <v-list-item :title="token.name" class="py-3 ps-md-10">
                                            <v-list-item-subtitle v-if="token.last_used_ago">
                                                Last used {{ token.last_used_ago }} 
                                            </v-list-item-subtitle>
                                            <template v-slot:append>
                                                <div class="d-flex flex-wrap">
                                                    <v-btn class="text-none text-decoration-underline" slim color="info" variant="text" text="Permissions" v-if="page.props.availablePermissions.length > 0" @click="manageApiTokenPermissions(token)"></v-btn>
                                                    <v-btn-icon-text color="danger" @click="confirmApiTokenDeletion(token)">
                                                        <mdi-icon>delete</mdi-icon>
                                                    </v-btn-icon-text>
                                                </div>
                                            </template>
                                        </v-list-item>
                                    </template>
                                </v-list>
                            </div>
                            
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </div>

        <div>
            <v-dialog v-model="displayingToken" width="auto" @afterLeave="displayingToken = false">
                <v-card max-width="100%" width="700">
                    <v-card-text>
                        <v-card-title>API Token</v-card-title>
                        <div class="">
                            <div class="text-subtitle-2 text-secondary pb-4">Please copy your new API token. For your security, it won't be shown again.</div>
                            <v-sheet v-if="page.props.jetstream?.flash?.token" class="py-2 px-4">
                                {{ page.props.jetstream.flash.token }}
                            </v-sheet>
                        </div>
                    </v-card-text>
                    <v-card-actions-form-modal>
                        <v-btn-modal
                            text="Close"
                            color="btn-secondary"
                            @click="displayingToken = false"
                        ></v-btn-modal>
                    </v-card-actions-form-modal>
                </v-card>
            </v-dialog>

            <!-- API Token Permissions manage Modal -->
            <v-dialog v-model="updateingApiToken" width="auto" @afterLeave="updateingApiToken = false">
                <v-card max-width="100%" width="500">
                    <v-card-title>API Token Permission</v-card-title>
                    <v-card-subtitle>Name: ({{ managingPermissionsFor?.name }})</v-card-subtitle>
                    <v-card-text>
                        <div class="">
                            <v-row dense>
                                <v-col cols="6" v-for="permission in page.props.availablePermissions" :key="permission">
                                    <v-checkbox color="primary" :label="permission" hide-details v-model="updateApiTokenForm.permissions" :value="permission"></v-checkbox>
                                </v-col>
                            </v-row>
                        </div>
                    </v-card-text>
                    <v-card-actions-modal>
                        <v-btn
                            text="Cancel"
                            color="btn-secondary"
                            @click="updateingApiToken = false"
                        ></v-btn>
                        <v-btn
                            :disabled="updateApiTokenForm.processing"
                            :loading="updateApiTokenForm.processing"
                            @click="updateApiToken"
                            text="Save"
                        ></v-btn>
                    </v-card-actions-modal>
                </v-card>
            </v-dialog>

            <!-- API Token delete confirm Modal -->
            <v-dialog v-model="deletingApiToken" width="auto" @afterLeave="deletingApiToken = false">
                <v-card max-width="100%" width="500">
                    <v-card-title>Delete API Token</v-card-title>
                    <v-card-subtitle>Are you sure you want to delete this API Token?</v-card-subtitle>
                    <v-card-text class="py-2"></v-card-text>
                    <v-card-actions-modal>
                        <v-btn
                            text="Cancel"
                            color="btn-secondary"
                            @click="deletingApiToken = false"
                        ></v-btn>
                        <v-btn
                            :disabled="deleteApiTokenForm.processing"
                            :loading="deleteApiTokenForm.processing"
                            @click="deleteApiToken"
                            color="error"
                            text="Delete"
                        ></v-btn>
                    </v-card-actions-modal>
                </v-card>
            </v-dialog>
        </div>
    </div>
</template>
